import * as React from "react";
import HourlyRate from "./hourly_rate";
import AdditionalCost from "./additional_cost";
import Title from "../../../../../common/title/title";

import CurrencyDropdown from "../../../../../common/dropdown/currency_dropdown";

interface StepProps {
  droneData: {
    price: number;
    editCost?: number;
    travelCost?: number;
    currency?: "RON" | "EUR";
  };
  updateDroneData: (
    newData: Partial<{
      price: number;
      editCost?: number;
      travelCost?: number;
      currency?: "RON" | "EUR";
    }>
  ) => void;
}

const Prices: React.FC<StepProps> = ({ droneData, updateDroneData }) => {
  const [hourlyRate, setHourlyRate] = React.useState(droneData.price ?? 0);
  const [editCosts, setEditCosts] = React.useState(droneData.editCost ?? 0);
  const [travelCosts, setTravelCosts] = React.useState(droneData.travelCost ?? 0);
  const [currency, setCurrency] = React.useState<"RON" | "EUR">(droneData.currency || "RON");

  const handleRateChange = (newRate: number) => {
    setHourlyRate(newRate);
    updateDroneData({ price: newRate });
  };

  const handleCurrencyChange = (newCurrency: "RON" | "EUR") => {
    setCurrency(newCurrency);
    updateDroneData({ currency: newCurrency });
  };

  // Update parent state whenever edit or travel costs change
  React.useEffect(() => {
    updateDroneData({ editCost: editCosts, travelCost: travelCosts });
    // We intentionally omit updateDroneData as a dep to match prior set usage.
    // eslint-disable-next-line
  }, [editCosts, travelCosts]);

  // Example price range for display
  const priceRange = currency === "RON" ? "RON 200 - RON 1000" : "EUR 40 - EUR 200";

  return (
    <div className="w-full max-w-2xl mx-auto mt-8 px-4">
      <Title
        title="Set a daily price and optional offers"
        subtitle="Setting multiple price options makes your listing more competitive."
      />
      <div className="mt-6 space-y-4">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex-1 w-full">
            <HourlyRate rate={hourlyRate} onRateChange={handleRateChange} currency={currency} />
          </div>
          <CurrencyDropdown value={currency} onChange={handleCurrencyChange} />
        </div>
        <p className="text-sm text-gray-600 font-ubuntu text-left">
          Similar products for rent range between {priceRange}
        </p>
        <div className="text-left">
          <div className="w-full max-w-[480px]">
            <AdditionalCost
              label="Photo/video edit costs"
              cost={editCosts}
              onCostChange={setEditCosts}
              currency={currency}
            />
          </div>
          <div className="w-full max-w-[403px] mt-2">
            <AdditionalCost
              label="Travel costs/km"
              cost={travelCosts}
              onCostChange={setTravelCosts}
              unit="km"
              currency={currency}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Prices;
